import { useState, useCallback, useRef } from 'react';
import AdminProject from './AdminProject';
import { DefaultButton, PrimaryButton, IDropdownOption, Spinner, useTheme, Separator, Stack } from '@fluentui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createNewGuid } from '../../../../components/Utils';
import { ContentSection } from '../../../../components/LayoutElements';
import AdminProjectSourcesDropdown from './AdminProjectSourcesDropdown';
import { GET_ADMIN_PROJECT_AND_TASKS_FOR_CONFIG } from '../../../../constants/queryKeys';
import { ApiCalls } from '../../../../api/api';
import { AdminProject as AdminProjectType, TimeTask } from '../../../../api/generated/data-contracts';
import { useSetQueryData } from '../../../../hooks/useSetQueryData';

export function AdminProjects() {
    const theme = useTheme();
    const [selectedProjectSource, setSelectedProjectSource] = useState<IDropdownOption>();

    const defaultData = useRef([]).current;
    const { data: projects, isLoading: isLoadingProjects } = useQuery({
        queryKey: [GET_ADMIN_PROJECT_AND_TASKS_FOR_CONFIG],
        queryFn: async (): Promise<AdminProjectType[]> => {
            return ApiCalls.getAdminProjects({}).then(({ data }) =>
                data.toSorted((a, b) => {
                    return a.order < b.order ? -1 : 1;
                }),
            );
        },
        suspense: true,
        placeholderData: defaultData,
    });

    const setProjects = useSetQueryData<AdminProjectType[]>([GET_ADMIN_PROJECT_AND_TASKS_FOR_CONFIG]);

    const ReorderProject = useCallback(
        (p: AdminProjectType, direction: number) => {
            setProjects(projects => {
                let neworder: number = -1;

                if (direction > 0) {
                    neworder = p.order < projects.length ? p.order + 1 : projects.length;
                } else {
                    neworder = p.order > 1 ? p.order - 1 : 1;
                }

                let ordernumber: number = 1;
                for (let i = 0; i < projects.length; i++) {
                    const currentProject = projects[i];
                    if (neworder === ordernumber) {
                        ordernumber = ordernumber + 1;
                    }

                    if (currentProject.id === p.id) {
                        projects[i] = {
                            ...currentProject,
                            order: neworder,
                        };
                    } else {
                        projects[i] = {
                            ...currentProject,
                            order: ordernumber,
                        };
                        ordernumber = ordernumber + 1;
                    }
                }
                return [...projects].sort((a, b) => {
                    return a.order < b.order ? -1 : 1;
                });
            });
        },
        [setProjects],
    );

    const ProjectUpdated = useCallback(
        (id: string, name: string, closed: boolean) => {
            setProjects(projects =>
                projects
                    .map(project => {
                        if (project.id === id) {
                            return {
                                ...project,
                                name,
                                closed,
                            };
                        }
                        return project;
                    })
                    .sort((a, b) => {
                        return a.order < b.order ? -1 : 1;
                    }),
            );
        },
        [setProjects],
    );

    const TaskUpdated = useCallback(
        (tsk: TimeTask, prj: AdminProjectType) => {
            setProjects(projects => {
                return projects.map(project => {
                    if (project.id === prj.id) {
                        const isNewTask = !project.tasks.some(t => t.id === tsk.id);
                        return {
                            ...project,
                            tasks: isNewTask
                                ? [...project.tasks, tsk]
                                : project.tasks.map(task => {
                                      if (task.id === tsk.id) {
                                          return {
                                              ...task,
                                              name: tsk.name,
                                              closed: tsk.closed,
                                              order: tsk.order,
                                          };
                                      }
                                      return task;
                                  }),
                        };
                    }
                    return project;
                });
            });
        },
        [setProjects],
    );

    const { mutate, isLoading: loadingSetAdminProjects } = useMutation(async () => {
        await ApiCalls.setAdminProjects(projects);
        if (selectedProjectSource?.key) {
            await ApiCalls.setAdminProjectsSource({ projectSourceId: String(selectedProjectSource.key) });
        }
    });

    const saveChanges = useCallback(async () => {
        mutate();
    }, [mutate]);

    const AddProjectClick = useCallback(() => {
        setProjects(projects => {
            const newproject: AdminProjectType = {
                id: createNewGuid(),
                name: 'New Project',
                closed: false,
                order: projects.length + 1,
                metaData: '',
                priority: projects.length + 1,
                tasks: [],
            };
            return [...projects, newproject];
        });
    }, [setProjects]);

    const onChange = useCallback((event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
        setSelectedProjectSource(item);
    }, []);

    if (isLoadingProjects || loadingSetAdminProjects) {
        return (
            <Stack className="logindialog" horizontalAlign="center" verticalAlign="center">
                <Spinner label="Loading..." />
            </Stack>
        );
    }

    return (
        <>
            <ContentSection noPaddingBottom>
                <Stack
                    horizontal
                    horizontalAlign="space-between"
                    verticalAlign="end"
                    tokens={{ childrenGap: 20 }}
                    styles={{
                        root: {
                            width: '100%',
                            maxWidth: 692,
                            margin: '0 auto',
                            borderBottom: `1px solid ${theme.semanticColors.variantBorder}`,
                            padding: '0 16px 16px',
                        },
                    }}
                >
                    <Stack.Item align="center">
                        <AdminProjectSourcesDropdown selectedProjectSource={selectedProjectSource} callback={onChange} />
                    </Stack.Item>
                    <Stack.Item align="end">
                        <Stack horizontal tokens={{ childrenGap: 10 }}>
                            <PrimaryButton onClick={saveChanges} text="Save Changes" />
                            <DefaultButton
                                onClick={() => {
                                    // TODO: fix link
                                    window.location.href = window.location.href.substring(0, window.location.href.toLowerCase().indexOf('/admin') + 6);
                                }}
                                text="Cancel"
                            />
                        </Stack>
                    </Stack.Item>
                </Stack>
            </ContentSection>

            <ContentSection>
                <Stack tokens={{ childrenGap: 10 }}>
                    {projects.map((proj, index) => (
                        <AdminProject
                            key={index}
                            ReorderProject={ReorderProject}
                            Tasks={proj.tasks}
                            ProjectUpdated={ProjectUpdated}
                            TaskUpdated={TaskUpdated}
                            Project={proj}
                        />
                    ))}
                    <Separator>
                        <Stack tokens={{ childrenGap: 20 }} horizontal>
                            <h4>
                                <DefaultButton text="Add Project" onClick={AddProjectClick}></DefaultButton>
                            </h4>
                        </Stack>
                    </Separator>
                </Stack>
            </ContentSection>
        </>
    );
}
